<script lang="ts" setup>
import { computed } from 'vue'
import type { RunCost } from '@/types'
import { formatDuration } from '../utils/runFormatters'

const props = defineProps<{
  cost?: RunCost
  /** How long this waited for a place before any of it ran. Omitted for a service, which never waits. */
  queueWaitMs?: number | null
  /** Its own duration, against which the transaction time gives the concurrency reached. */
  processingMs?: number
}>()

const whole = (n: number) => n.toLocaleString()

const bytes = (n: number) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB`
  : n >= 1024 ? `${Math.round(n / 1024)} KB`
  : `${n} B`

/**
 * Time held for a permit, per send. The stored total sums across every send in flight at once, so it
 * runs to hours on a run that took minutes — printed raw it reads as a broken number, where per send it
 * answers the question actually being asked.
 */
function heldPerSend(totalMs: number): string {
  if (!props.cost?.vendorCalls) return '--'
  const each = totalMs / props.cost.vendorCalls
  return each < 1 ? '<1ms' : `${Math.round(each)}ms`
}

/** Sends a second the run actually reached — against a visible hold, this is what says "capped". */
const achieved = computed(() => {
  const worked = props.processingMs ?? 0
  if (!props.cost?.vendorCalls || worked <= 0) return null
  return (props.cost.vendorCalls / (worked / 1000)).toFixed(1)
})
</script>

<template>
  <dl v-if="cost" class="run-cost mb-0 small">
    <template v-if="queueWaitMs !== undefined">
      <dt>Queued</dt>
      <dd>{{ formatDuration(queueWaitMs) }}<span class="text-muted ms-2">before a place came free</span></dd>
    </template>

    <dt>Held by rate limit</dt>
    <dd>
      {{ heldPerSend(cost.throttledVendorMs) }} per send
      <span class="text-muted ms-2">
        vendor &middot; {{ heldPerSend(cost.throttledInternalMs) }} internal &middot;
        {{ heldPerSend(cost.throttledOrgMs) }} org &middot;
        {{ heldPerSend(cost.throttledPlatformMs) }} platform
      </span>
    </dd>

    <dt>Vendor calls</dt>
    <dd>
      {{ whole(cost.vendorCalls) }}
      <span v-if="achieved" class="text-muted ms-2">{{ achieved }} a second reached</span>
    </dd>

    <dt>Store</dt>
    <dd>
      {{ whole(cost.storeRequests) }} requests
      <span class="text-muted ms-2">{{ cost.requestCharge.toFixed(0) }} RU</span>
    </dd>

    <dt>Payload</dt>
    <dd>{{ bytes(cost.blobBytes) }}<span class="text-muted ms-2">written</span></dd>

    <dt>Transaction time</dt>
    <dd>{{ formatDuration(cost.computeMs) }}<span class="text-muted ms-2">across every transaction at once</span></dd>
  </dl>

  <p v-else class="text-muted small mb-0">Nothing recorded — this run predates per-run cost.</p>
</template>

<style scoped lang="scss">
// Two columns that hold their shape as the numbers change width, so the labels stay a column and the
// figures line up under each other rather than dancing as a run progresses.
.run-cost {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.25rem 1.25rem;

  dt {
    font-weight: 500;
    color: var(--bs-secondary-color);
  }

  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
  }
}
</style>
